import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { supabase } from '../../config/supabaseClient';

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  return (sessionResp as any)?.data?.session?.access_token || '';
};

const withAuth = async (init: RequestInit = {}, adminToken?: string): Promise<RequestInit> => {
  const token = await getToken();
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(adminToken ? { 'x-queue-admin': adminToken } : {}),
      ...(init.headers || {}),
    },
  };
};

const QueueAdminPanel: React.FC = () => {
  const [adminToken, setAdminToken] = useState<string>(() => sessionStorage.getItem('QUEUE_ADMIN_TOKEN') || '');
  const [jobId, setJobId] = useState('');
  const [job, setJob] = useState<any | null>(null);
  const [logs, setLogs] = useState<string[] | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bulkIds, setBulkIds] = useState('');
  const [replayPrefix, setReplayPrefix] = useState('replay');
  const [replayPriority, setReplayPriority] = useState<number | ''>('');

  useEffect(() => {
    if (adminToken) sessionStorage.setItem('QUEUE_ADMIN_TOKEN', adminToken);
  }, [adminToken]);

  const api = useMemo(() => ({
    async getJob(id: string) {
      const url = `${API_BASE_URL}/api/admin/queue/ops/job/${encodeURIComponent(id)}`;
      const resp = await fetch(url, await withAuth({}, adminToken));
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    async bulkRetry(ids: string[]) {
      const url = `${API_BASE_URL}/api/admin/queue/ops/retry`;
      const resp = await fetch(url, await withAuth({ method: 'POST', body: JSON.stringify({ jobIds: ids }) }, adminToken));
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    async replay(id: string, prefix?: string, priority?: number) {
      const url = `${API_BASE_URL}/api/admin/queue/ops/replay`;
      const body: any = { jobId: id };
      if (prefix) body.prefix = prefix;
      if (typeof priority === 'number') body.priority = priority;
      const resp = await fetch(url, await withAuth({ method: 'POST', body: JSON.stringify(body) }, adminToken));
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
  }), [adminToken]);

  const handleFetch = useCallback(async () => {
    setError(null); setPending(true);
    try {
      const data = await api.getJob(jobId.trim());
      setJob(data.job);
      setLogs(data.logs?.logs || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch job');
    } finally { setPending(false); }
  }, [api, jobId]);

  const handleBulkRetry = useCallback(async () => {
    setError(null); setPending(true);
    try {
      const ids = bulkIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      await api.bulkRetry(ids);
    } catch (e: any) {
      setError(e?.message || 'Failed to bulk retry');
    } finally { setPending(false); }
  }, [api, bulkIds]);

  const handleReplay = useCallback(async () => {
    setError(null); setPending(true);
    try {
      const prio = typeof replayPriority === 'number' ? replayPriority : undefined;
      await api.replay(jobId.trim(), replayPrefix || undefined, prio);
    } catch (e: any) {
      setError(e?.message || 'Failed to replay job');
    } finally { setPending(false); }
  }, [api, jobId, replayPrefix, replayPriority]);

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded shadow">
      <h1 className="text-2xl font-bold mb-4">Queue Admin Panel</h1>
      <p className="text-sm text-gray-600 mb-6">Requires premium auth and x-queue-admin token.</p>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">x-queue-admin Token</label>
        <input className="w-full border rounded px-3 py-2" value={adminToken} onChange={e => setAdminToken(e.target.value)} placeholder="Enter admin token" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="font-semibold mb-2">Job Drilldown</h2>
          <input className="w-full border rounded px-3 py-2 mb-2" value={jobId} onChange={e => setJobId(e.target.value)} placeholder="Job ID" />
          <button disabled={pending || !jobId} onClick={handleFetch} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Fetch</button>
          {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
          {job && (
            <div className="mt-4">
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-64">{JSON.stringify(job, null, 2)}</pre>
              <h3 className="mt-3 font-semibold">Logs</h3>
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-48">{(logs || []).join('\n')}</pre>
            </div>
          )}
        </div>

        <div>
          <h2 className="font-semibold mb-2">Bulk Retry / Replay</h2>
          <textarea className="w-full border rounded px-3 py-2 mb-2" rows={5} value={bulkIds} onChange={e => setBulkIds(e.target.value)} placeholder="Enter job IDs separated by comma or newline" />
          <div className="flex gap-2 mb-4">
            <button disabled={pending || !bulkIds.trim()} onClick={handleBulkRetry} className="px-4 py-2 bg-yellow-600 text-white rounded disabled:opacity-50">Bulk Retry</button>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm">Replay Selected Job</label>
            <div className="flex gap-2 mt-2">
              <input className="flex-1 border rounded px-3 py-2" value={replayPrefix} onChange={e => setReplayPrefix(e.target.value)} placeholder="Prefix (e.g., replay)" />
              <input className="w-36 border rounded px-3 py-2" value={replayPriority} onChange={e => setReplayPriority(e.target.value === '' ? '' : Number(e.target.value))} placeholder="Priority" />
              <button disabled={pending || !jobId} onClick={handleReplay} className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50">Replay</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueueAdminPanel;
